import { find } from 'lodash-es';

export interface TPermission<TActions, TDenialReasons> {
  action: TActions;
  allowed: boolean;
  denialReason: TDenialReasons;
}

// We use the string literal type here to limit valid values to a specific list of strings
type Contexts =
  | 'BUYER_RFP'
  | 'GROUP_MASTER_IMPACT'
  | 'GROUP_MEMBER_IMPACT'
  | 'PURCHASE_PLANNING'
  | 'APTITUDE'
  | 'GROUP_PROFILE'
  | 'REVIEW_CONTRACTS'
  | 'SELLER_RFP_RESPONSE';

// These types should match the valid types for the corresponding context in miredot
export type ResponseActions =
  | 'ADD_OFFER'
  | 'UPDATE_OFFER'
  | 'REMOVE_OFFER'
  | 'MODIFY_ATTACHMENT'
  | 'MODIFY_OFFER_TIER'
  | 'UPDATE_OFFER_PRODUCT_PRICES'
  | 'SUBMIT_OFFERS'
  | 'VIEW_OFFER_IMPACT'
  | 'RETRACT_OFFERS'
  | 'CLONE_OFFER';

export type ResponseDenialReasons =
  | 'OfferAlreadySubmitted'
  | 'NoSubmitOfferRole'
  | 'NoActiveCategoryAddendum'
  | 'NoValidCpaTemplate'
  | 'NoSellerUnspcs'
  | 'PricingNotComplete'
  | 'RestrictedFromCategory'
  | 'NoCrossReferences'
  | 'NoAdditionalOfferRequested'
  | 'SellerExcludedByBuyer';

/**
 * When we add new contexts, simply extend this class to handle the new permissions
 */
class BasePermissions<TAction, TDenialReason> {
  protected permissions: TPermission<TAction, TDenialReason>[];

  constructor(protected api) {
    //
  }

  arePermissionsLoaded() {
    return !!this.permissions;
  }

  // It seems like these classes should be able to load their own data, which would ensure permissions are passed to the
  // right subclass
  // But the methods to load permissions in the users api currently takes a variable number of parameters.
  // We could fix it to make them all take the same parameters, even if some are null, or we could just pass them as an
  // object
  // For now, I'm just going to let users call the api directly to load permissions and pass them to the appropriate
  // service
  // abstract loadPermissions (params);

  setPermissions(permissions) {
    this.permissions = permissions;
  }

  private findPermission(actionName: TAction) {
    return find(this.permissions, function (permission) {
      return permission.action === actionName;
    });
  }

  isActionAllowed(actionName: TAction) {
    var permissionObj = this.findPermission(actionName);

    return permissionObj && permissionObj.allowed;
  }

  getDenialReason(actionName: TAction): TDenialReason {
    let permission = this.findPermission(actionName);

    if (permission) {
      return permission.denialReason;
    } else {
      return null;
    }
  }
}

/**
 * Permissions related to the SELLER_RFP_RESPONSE context
 */
export class ResponsePermissions extends BasePermissions<
  ResponseActions,
  ResponseDenialReasons
> {
  constructor(api) {
    'ngInject';

    super(api);
  }

  // loadPermissions ({ userId, rfpId, sellerId }) {
  //   return this.api.users.getResponsePermissions(userId, rfpId, sellerId);
  // }

  canAddOffer() {
    return this.isActionAllowed('ADD_OFFER');
  }

  canUpdateOffer() {
    return this.isActionAllowed('UPDATE_OFFER');
  }

  canRemoveOffer() {
    return this.isActionAllowed('REMOVE_OFFER');
  }

  canCloneOffer() {
    return this.isActionAllowed('CLONE_OFFER');
  }

  canUpdateOfferPricing() {
    return this.isActionAllowed('UPDATE_OFFER_PRODUCT_PRICES');
  }

  canSubmitOffer() {
    // console.log(this.permissions);
    // console.log(this.isActionAllowed('SUBMIT_OFFERS'));
    return this.isActionAllowed('SUBMIT_OFFERS');
  }

  canViewOfferImpact() {
    return this.isActionAllowed('VIEW_OFFER_IMPACT');
  }

  canRetractResponse() {
    return this.isActionAllowed('RETRACT_OFFERS');
  }

  canModifyAttachments() {
    return this.isActionAllowed('MODIFY_ATTACHMENT');
  }

  canModifyOfferTier() {
    return this.isActionAllowed('MODIFY_OFFER_TIER');
  }
}

type PurchasePlanningActions =
  | 'CHANGE_SELLERS'
  | 'CHANGE_MARKET_SHARE'
  | 'CHANGE_DURATION_PRICING_TYPE'
  | 'CHANGE_ALLOCATION'
  | 'CHANGE_ALLOCATION_LOCK'
  | 'LOAD_PLAN'
  | 'SAVE_PURCHASE_PLAN'
  | 'CHANGE_CROSSES'
  | 'CHANGE_CONTRACT_EFFECTIVE_DATE'
  | 'PUBLISH_PURCHASE_PLAN';

/**
 * Permissions related to the PURCHASE_PLANNING context
 */
export class PurchasePlanningPermissions extends BasePermissions<
  PurchasePlanningActions,
  string
> {
  constructor(api) {
    'ngInject';

    super(api);
  }

  // loadPermissions ({ userId, rfpId, sellerId }) {
  //   return this.api.users.getResponsePermissions(userId, rfpId, sellerId);
  // }

  canAddRemoveSellers() {
    return this.isActionAllowed('CHANGE_SELLERS');
  }

  canChangeMarketShare() {
    return this.isActionAllowed('CHANGE_MARKET_SHARE');
  }

  canChangeDurationAndPricingType() {
    return this.isActionAllowed('CHANGE_DURATION_PRICING_TYPE');
  }

  canChangeAllocations() {
    return this.isActionAllowed('CHANGE_ALLOCATION');
  }

  canChangeLocks() {
    return this.isActionAllowed('CHANGE_ALLOCATION_LOCK');
  }

  /**
   * This is based on the edit plan role and determines whether the user can load a mix
   * For leaders, if LOAD_PLAN is true, then SAVE will also be true
   * For partners, if LOAD_PLAN is true, SAVE will also be false (for now) since we don't allow partners to save changes
   *
   * @return {boolean} Whether or not the user can load a mix or plan
   */
  canLoadMix(): boolean {
    return this.isActionAllowed('LOAD_PLAN');
  }

  /**
   * This is based on the edit plan role and determines whether the user can save changes to a plan
   *
   * @return {boolean} Whether or not the user can save a plan
   */
  canSavePlan() {
    return this.isActionAllowed('SAVE_PURCHASE_PLAN');
  }

  canChangeCrosses() {
    return this.isActionAllowed('CHANGE_CROSSES');
  }

  canChangeContractEffectiveDate() {
    return this.isActionAllowed('CHANGE_CONTRACT_EFFECTIVE_DATE');
  }

  canPublishPurchasePlan() {
    return this.isActionAllowed('PUBLISH_PURCHASE_PLAN');
  }
}
